import { isArrowType, isLineType, PathCollapsingRule } from '@ardoq/api-types';
import { isStrictColorHex } from '@ardoq/color-helpers';
import { logError } from '@ardoq/logging';
import { isValidArdoqId } from '@ardoq/validation';
import { defaultCollapsedPathReferenceStyle } from 'viewpointBuilder/collapsePathsTab/createEmptyPathCollapsingRule';

type ReferenceTypeStyle = PathCollapsingRule['referenceStyle'];

const isSafeReferenceStyle = ({
  color,
  line,
  lineBeginning,
  lineEnding,
  svgStyle,
}: ReferenceTypeStyle): boolean =>
  isStrictColorHex(color) &&
  isLineType(line) &&
  isArrowType(lineBeginning) &&
  isArrowType(lineEnding) &&
  svgStyle === null;

const toSafeReferenceStyle = ({
  color,
  line,
  lineBeginning,
  lineEnding,
}: ReferenceTypeStyle): ReferenceTypeStyle => ({
  color: isStrictColorHex(color)
    ? color
    : defaultCollapsedPathReferenceStyle.color,
  line: isLineType(line) ? line : defaultCollapsedPathReferenceStyle.line,
  lineBeginning: isArrowType(lineBeginning)
    ? lineBeginning
    : defaultCollapsedPathReferenceStyle.lineBeginning,
  lineEnding: isArrowType(lineEnding)
    ? lineEnding
    : defaultCollapsedPathReferenceStyle.lineEnding,
  svgStyle: defaultCollapsedPathReferenceStyle.svgStyle,
});

/** Rules decoded before v8 have no id; the router assigns one after normalization. */
const isSafeReferenceTypeId = (referenceTypeId: string): boolean =>
  !referenceTypeId || isValidArdoqId(referenceTypeId);

const isSafeRule = ({
  referenceTypeId,
  referenceStyle,
}: PathCollapsingRule): boolean =>
  isSafeReferenceTypeId(referenceTypeId) &&
  isSafeReferenceStyle(referenceStyle);

/**
 * Both the id and the style reach a global stylesheet via the virtual reference
 * type - the id as a selector, the style as a declaration - but arrive from URL
 * and slide data any user can craft (APPSEC-5494). The collapse-paths UI only
 * ever writes an `ardoqId()` and the default style, so nothing else is trusted.
 * A forged id is dropped rather than replaced, since minting one here would run
 * on every normalization and `getLoadedStateHash` would never settle.
 */
export const sanitizePathCollapsingRules = (
  rules: PathCollapsingRule[]
): PathCollapsingRule[] => {
  if (!rules || rules.every(isSafeRule)) return rules;

  logError(Error('Unsafe path collapsing rule'));
  return rules
    .filter(({ referenceTypeId }) => isSafeReferenceTypeId(referenceTypeId))
    .map(rule => ({
      ...rule,
      referenceStyle: toSafeReferenceStyle(rule.referenceStyle),
    }));
};
